import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ListItem, ListItemButton, Switch, Tooltip, Typography, styled, switchClasses } from "@mui/material";
import FormSubmitButton from "@/components/forms/FormSubmitButton";
import Form from "@/components/forms/Form";
import { ISelectItem } from "@/components/forms/inputs/Select";
import { ConfigurationSourceData, ConfigurationSourceDataAllocation, Institution, Participant, ParticipantSourceDataAllocationUpdate } from "@/api/dtos";
import { IReturn } from "@servicestack/client";
import { GroupedVirtuoso } from "react-virtuoso";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Checkbox from "@/components/forms/inputs/Checkbox";
import { DateTime } from "luxon";
import { IFormSubmit, useFormActions } from "@ngt/forms-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FormError } from "@/errors";


const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogTitle-root': {
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'space-between',
        alignItems: 'center',

        '& > div': {
        display: 'flex',
        alignItems: 'center'
        }
    },
    '& .MuiDialogContent-root': {
        padding: theme.spacing(0),
        minHeight: theme.spacing(12)
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'
}));

const AllocationGroupBox = styled(ListItem)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minHeight: theme.spacing(7.5)
}));

const AllocationUpdateBox = styled(ListItemButton)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: theme.spacing(7.5)
}));

const AllocationCheckboxBox = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(2.5),
    maxWidth: theme.spacing(2.5)
}));

const AllocationStudyNumberBox = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(10),
    maxWidth: theme.spacing(10)
}));

const AllocationEnrollmentBox = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(11.25),
    maxWidth: theme.spacing(11.25)
}));

const AllocationConfigurationSourceDataBox = styled(Box)(({ theme }) => ({
    flex: '1 1 50%',
    display: 'flex',
    justifyContent: 'space-between',

    '& svg': {
        color: theme.palette.info.main
    }
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
    width: theme.spacing(7.25),
    height: theme.spacing(4.75),
    padding: theme.spacing(1.5),

    [`& .${switchClasses.switchBase}`]: {
        padding: theme.spacing(1.125),

        [`&.${switchClasses.checked}`]: {
            transform: `translateX(${theme.spacing(2.5)})`
        },
    },

    [`& .${switchClasses.thumb}`]: {
        width: theme.spacing(2.5),
        height: theme.spacing(2.5)
    }
}));

export interface IParticipantSourceDataAllocationSelectionForm<TData extends object = any> {
    data?: TData;
    allocations?: ConfigurationSourceDataAllocation[];
    participantSourceDataAllocations: ParticipantSourceDataAllocationUpdate[]
}

interface IParticipantSourceDataAllocationSelectionListProps {
    participantSourceDataAllocations?: ParticipantSourceDataAllocationUpdate[];
    sourceDataOptions?: Record<number, ConfigurationSourceData>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    scroller?: HTMLElement;
    showUnchanged?: boolean;
}

interface  IParticipantSourceDataAllocationUpdate extends ParticipantSourceDataAllocationUpdate {
    institution?: Institution;
    participant?: Participant;
    existingSourceDataOption?: ConfigurationSourceData;
    newSourceDataOption?: ConfigurationSourceData;
    index: number;
}

const ParticipantSourceDataAllocationSelectionList: FC<IParticipantSourceDataAllocationSelectionListProps> = ({
    sourceDataOptions,
    participantSourceDataAllocations,
    institutions,
    participants,
    scroller,
    showUnchanged
}) => {
    const { data, groupCounts, groupNames} = useMemo(() => {
        if (!institutions || !participants || !participantSourceDataAllocations || !sourceDataOptions) {
            return {
                groupCounts: [],
                groupNames: [],
                data: []
            };
        }

        let groups = participantSourceDataAllocations?.map((x, i) => ( {
            ...x,
            index: i,
            newSourceDataOption: sourceDataOptions && x.newAllocation?.configurationSourceDataId ?
                sourceDataOptions[x.newAllocation.configurationSourceDataId] :
                undefined,
            existingSourceDataOption: sourceDataOptions && x.existingAllocation?.configurationSourceDataId ?
                sourceDataOptions[x.existingAllocation.configurationSourceDataId] :
                undefined,
            institution: institutions && x.newAllocation?.institutionId ?
                institutions[x.newAllocation.institutionId] :
                undefined,
            participant: participants && x.newAllocation?.participantId ?
                participants[x.newAllocation.participantId] :
                undefined,
        } as IParticipantSourceDataAllocationUpdate))
        .reduce<Record<number, IParticipantSourceDataAllocationUpdate[]>>((a, x) => {
            const institution = x.institution;

            if (!showUnchanged && !x.update) {
                return a;
            }

            if (!institution?.id) {
                return a;
            }

            if (!a[institution.id]) {
                a[institution.id] = [];
            }

            a[institution.id].push(x);

            return a;
        }, {});

        const groupKeys = Object.keys(groups).sort((a, b) => institutions![a as any]!.name! > institutions![b as any]!.name! ? 1 : -1 );

        const groupCounts = groupKeys.map(x => groups[x as any].length);

        const groupNames = groupKeys.map(x => groups[x as any][0].institution?.name)

        const data = groupKeys.reduce<IParticipantSourceDataAllocationUpdate[]>((a , b) => [...a, {} as any, ...groups[b as any]!], [])

        return {
            groupCounts,
            groupNames,
            data
        }
    }, [participants, institutions, participantSourceDataAllocations, sourceDataOptions, showUnchanged])

    const { setFieldValue } = useFormActions();

    if (!scroller) {
        return null;
    }

    return (
        <GroupedVirtuoso
            groupCounts={groupCounts}
            groupContent={(index) => {
                return (
                    <AllocationGroupBox>
                        {groupNames[index]}
                    </AllocationGroupBox>
                );
            }}
            data={data}
            customScrollParent={scroller}
            itemContent={(index, groupIndex, item, context) => {
                const propertyPath = `participantSourceDataAllocations[${item.index}].update`

                return (
                    <Box>
                        <AllocationUpdateBox 
                            onClick={() => {
                                setFieldValue<boolean>(propertyPath, c => !c, true, true, true, true);
                            }}
                            disabled={!item.update}
                        >
                            <AllocationCheckboxBox>
                                {
                                    item.update && (
                                        <InputOnlyField
                                            propertyPath={propertyPath}
                                            component={Checkbox}
                                        />
                                    )
                                }
                            </AllocationCheckboxBox>
                            <AllocationStudyNumberBox>
                                {item.participant?.studyNumber}
                            </AllocationStudyNumberBox>
                            <AllocationEnrollmentBox>
                                {item.participant?.enrolmentDate && DateTime.fromISO(item.participant.enrolmentDate).toFormat('dd/MM/yyyy')}
                            </AllocationEnrollmentBox>
                            <AllocationConfigurationSourceDataBox>
                                {item.existingSourceDataOption?.name}
                                {
                                    item.update && (
                                        <Tooltip 
                                            title={
                                                <>
                                                    <Typography variant="subtitle1">{item.existingSourceDataOption?.name}</Typography>
                                                    <Typography variant="caption">{item.existingSourceDataOption?.description}</Typography>
                                                </>
                                            }
                                        >
                                            <div>
                                                <FontAwesomeIcon icon={faQuestionCircle} fixedWidth />
                                            </div>
                                        </Tooltip>
                                    )
                                }
                            </AllocationConfigurationSourceDataBox>
                            <AllocationConfigurationSourceDataBox>
                                {
                                    item.update && (
                                        <>
                                            {item.newSourceDataOption?.name}
                                            <Tooltip 
                                                title={
                                                    <>
                                                        <Typography variant="subtitle1">{item.newSourceDataOption?.name}</Typography>
                                                        <Typography variant="caption">{item.newSourceDataOption?.description}</Typography>
                                                    </>
                                                }
                                            >
                                                <div>
                                                    <FontAwesomeIcon icon={faQuestionCircle} fixedWidth />
                                                </div>
                                            </Tooltip>
                                        </>
                                    )
                                }
                            </AllocationConfigurationSourceDataBox>
                        </AllocationUpdateBox>
                        <Divider />
                    </Box>
                )
            }}
        />
    )
}

interface IParticipantSourceDataAllocationSelectionDialogProps<TData extends object = any> {
    formData?: IParticipantSourceDataAllocationSelectionForm<TData>
    sourceDataOptions?: Record<number, ConfigurationSourceData>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    open?: boolean;
    onClose?: () => void;
    onSave?: (data?: TData, allocations?: ConfigurationSourceDataAllocation[], updates?: ParticipantSourceDataAllocationUpdate[] ) => Promise<any>;
}

const ParticipantSourceDataAllocationSelectionDialog = <TData extends object = any>({
    formData,
    institutions,
    participants,
    sourceDataOptions,
    open,
    onClose,
    onSave
}: IParticipantSourceDataAllocationSelectionDialogProps<TData>) => {
    const [scroller, setScroller] = useState<HTMLElement>();
    const [showUnchanged, setShowUnchanged] = useState(false);

    useEffect(() => {
        if (!open) {
            setShowUnchanged(false);
        }
    }, [open, setShowUnchanged]);

    const submit = useCallback<IFormSubmit<IParticipantSourceDataAllocationSelectionForm<TData>, FormError>>(async (formState, formActions) => {
        await onSave?.(formState.value?.data, formState.value?.allocations, formState.value?.participantSourceDataAllocations);

        onClose?.();

        return {};
    }, [onSave, onClose]);

    return (
        <Form
            initialValue={formData}
            onSubmit={submit}
        >
            <StyledDialog
                //onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open ?? false}
                maxWidth="xl"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>
                    Participant Source Data Allocations
                    <Box>
                        <Box>
                            <Typography variant="body1">Show Unchanged Allocations</Typography>
                        </Box>
                        <StyledSwitch 
                            value={showUnchanged} 
                            onChange={(_, checked) => setShowUnchanged(checked)} 
                        />
                    </Box>
                </DialogTitle>
                <DialogContent dividers ref={(ref) => setScroller(ref as HTMLElement)}>
                    {
                        open && (
                            <ParticipantSourceDataAllocationSelectionList 
                                participants={participants}
                                institutions={institutions}
                                participantSourceDataAllocations={formData?.participantSourceDataAllocations}
                                sourceDataOptions={sourceDataOptions}
                                scroller={scroller}
                                showUnchanged={showUnchanged}
                            />
                        )
                    }
                </DialogContent>
                <DialogActions>
                        <FormButtons>
                            <FormSubmitButton
                                variant="contained"
                                color="primary"
                            >
                                Save
                            </FormSubmitButton>
                            { 
                                onClose && (
                                    <Button
                                        variant="contained"
                                        color="inherit"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </Button>
                                )
                            }
                        </FormButtons>
                </DialogActions>
            </StyledDialog>
        </Form>
    );
}

export default ParticipantSourceDataAllocationSelectionDialog;